import React, { useState, useEffect } from 'react';
import { ProgressCircular } from 'components';
import { Modal, Box, IconButton } from '@mui/material';
import { Download, OpenInNew, Close } from '@mui/icons-material';
import styles from './Viewer.module.css';

const Viewer = ({ doc, open, onClose }) => {
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		if (doc) {
			if (doc.unsecure) {
				setLoading(false);
			} else {
				setLoading(true);
			}
		}
	}, [doc]);

	const renderHeader = (fullViewUrl = null) => (
		<div className={styles.custom_header}>
			<span className={styles.file_name}>{doc.name}</span>
			<div className={styles.header_actions}>
				<IconButton 
					className={styles.icon_btn}
					onClick={() => {
						const link = document.createElement('a');
						link.href = doc.uri;
						link.download = doc.name || 'download';
						document.body.appendChild(link);
						link.click();
						document.body.removeChild(link);
					}}
					title="Download"
					>
					<Download />
				</IconButton>
				{fullViewUrl && (
					<IconButton 
						className={styles.icon_btn}
						onClick={() => window.open(fullViewUrl, '_blank')}
						title="Full View"
					>
						<OpenInNew />
					</IconButton>
				)}
				<IconButton 
					className={styles.icon_btn_close}
					onClick={onClose}
					title="Close"
				>
					<Close />
				</IconButton>
			</div>
		</div>
	);

	const renderContent = () => {
		if (doc.unsecure) {
			const officeViewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(doc.uri)}`;
			
			return (
				<>
					{/* Use the new icon-based header */}
					{renderHeader(officeViewerUrl)}
					<iframe
						src={officeViewerUrl}
						style={{
							width: '100%',
							height: 'calc(100% - 50px)',
							border: 'none',
							minHeight: '550px'
						}}
						title={doc.name}
						onLoad={() => setLoading(false)}
						className={styles.viewer_ms_doc}
					/>
				</>
			);
		}

		let fileType = doc?.type.split('/')[0];
		switch (fileType) {
			case 'image':
				return (
					<>
						{renderHeader(doc.uri)}
						<img
							src={doc.uri}
							alt={doc.name}
							style={{
								width: '100%',
								height: 'calc(100% - 50px)',
								objectFit: 'contain',
								backgroundColor: '#f8f9fa'
							}}
							onLoad={() => setLoading(false)}
						/>
					</>
				);
			case 'video':
				return (
					<>
						{renderHeader(doc.uri)}
						<video
							controls
							style={{
								width: '100%',
								height: 'calc(100% - 50px)',
								backgroundColor: '#000'
							}}
							onLoadedData={() => setLoading(false)}
						>
							<source src={doc.uri} type={doc.type} />
							Your browser does not support the video tag.
						</video>
					</>
				);
			case 'application': // pdf has 'application' as the first part of its MIME type
				let subtype = doc.type.split('/')[1]; // gets the subtype of the MIME type
				if (subtype === 'pdf') {
					const pdfUrl = window.location.origin + doc.uri;
					return (
						<>
							{renderHeader(pdfUrl)}
							<object
								data={pdfUrl}
								type={doc.type}
								style={{
									width: '100%',
									height: 'calc(100% - 50px)',
									minHeight: '550px'
								}}
								className={`${styles.viewer_ms_doc} ${styles.full_height} ${styles.full_width}`}
								onLoad={() => setLoading(false)}
							>
								<embed src={doc.uri} type={doc.type} />
								Sorry, your browser does not support embedded PDFs.
							</object>
						</>
					);
				} else {
					console.log(
						'This is an application file of type: ' + subtype,
					);
				}
				break;
			default:
				console.log('File type not recognized.');
				break;
		}
	};

	return (
		<>
			<Modal
				open={open}
				onClose={onClose}
				aria-labelledby="file-preview-modal-title"
				aria-describedby="file-preview-modal-description"
			>
				<Box className={styles.viewer_modal}>
					{loading && <ProgressCircular />}
					{renderContent()}
				</Box>
			</Modal>
		</>
	);
};

export default Viewer;
